import { BoxProps, chakra, Flex, type FormLabelProps } from '@chakra-ui/react';
import {
  CreatableSelect,
  Select,
  Size,
  OptionsOrGroups,
  GroupBase,
  ActionMeta,
  Props as ChakraReactSelectProps,
} from 'chakra-react-select';

import { IOption } from 'api-paths-cas';

import { JSX } from 'react';

import { CustomFormControl, DirectionProp } from '../CustomFormControl';
import { UndoEdit } from '../../iconButtons/UndoEdit';
import { withConfirm } from '../withConfirm';

interface ICombobox<T> extends DirectionProp {
  options: OptionsOrGroups<T, GroupBase<T>>;
  id: string;
  value?: string[];
  placeholder?: string;
  onChange?: (newValue: unknown, actionMeta: ActionMeta<unknown>) => void;
  onCreateOption?: (option: string) => boolean;
  size?: Size;
  selectProps?: ChakraReactSelectProps;
  mt?: BoxProps['mt'];
  mb?: BoxProps['mb'];
  label?: string;
  labelProps?: FormLabelProps;
  name?: string;
  subText?: string;
  errorText?: string;
  helperText?: string;
  canUndo?: boolean;
  onUndo?: () => void;
  isCreatable?: boolean;
  isMulti?: boolean;
  isClearable?: boolean;
  isDisabled?: boolean;
}

const StyledSelect = chakra(Select, {
  baseStyle: {
    margin: 0,
    padding: 0,
    width: '100%',
  },
});
const StyledCreatableSelect = chakra(CreatableSelect, {
  baseStyle: {
    margin: 0,
    padding: 0,
    width: '100%',
  },
});

export function getDefaultValue(props: ICombobox<IOption>): IOption[] {
  const groups = props.options.filter((o): o is GroupBase<IOption> => 'options' in o);
  if (groups.length > 0) {
    const allGroupOptions = groups.flatMap(group => group.options);
    return props?.value ? allGroupOptions.filter(o => props.value?.includes(o.value)) : [];
  }

  const flatOptions = props.options.filter((o): o is IOption => !('options' in o));
  return props?.value ? flatOptions.filter(o => props.value?.includes(o.value)) : [];
}

/**
 * Combination selector that implements chakra-react-select with configurable callback functions, state management
 */
export function Combobox(props: ICombobox<IOption>): JSX.Element {
  const defaultOptions = getDefaultValue(props);
  const selectInputId = `${props.id}-input-field`;

  const componentProps = {
    ...props,
    closeMenuOnSelect: !props.isMulti,
    value: defaultOptions,
    inputId: selectInputId,
    // instanceId prevents hydration mismatch by ensuring consistent IDs between server and client
    instanceId: props.id,
    openMenuOnFocus: false, // for consistency with native Select
  };

  return (
    <CustomFormControl
      id={props.id}
      controlProps={{
        isInvalid: !!props.errorText,
        mb: props.mb ?? 0,
        mt: props.mt ?? 0,
      }}
      direction={props.direction ?? 'column'}
      size={props.size}
      label={props.label}
      labelProps={{
        htmlFor: selectInputId,
        ...(props.labelProps ?? {}),
      }}
      subLabel={props.subText}
      helperText={props.helperText}
      error={props.errorText}
    >
      <Flex gap={{ base: 1, lg: 2 }} data-container={`combobox-container-${props.id}`}>
        <div data-container={`combobox-${props.id}`} style={{ width: '100%' }}>
          {props.isCreatable ? (
            <StyledCreatableSelect {...{ ...componentProps, ...props.selectProps }} />
          ) : (
            <StyledSelect {...{ ...componentProps, ...props.selectProps }} />
          )}
        </div>

        {props.canUndo ? (
          <UndoEdit id={props.id} data-selector={`combobox-undo-${props.id}`} onUndo={props.onUndo} size={props.size} />
        ) : null}
      </Flex>
    </CustomFormControl>
  );
}

export const ComboboxWithConfirm = withConfirm(Combobox);
